import { getCache, saveCache } from './storage';
import { debugLog } from './debug-log';

export interface ExchangeRateResponse {
  result: string;
  provider: string;
  documentation: string;
  terms_of_use: string;
  time_last_update_unix: number;
  time_last_update_utc: string;
  time_next_update_unix: number;
  time_next_update_utc: string;
  time_eol_unix: number;
  base_code: string;
  rates: {
    [currency: string]: number;
  };
}

export type Currency = 'USD' | 'JPY';

// Cache key for exchange rates
const EXCHANGE_RATE_CACHE_KEY = 'exchange_rates';
const EXCHANGE_RATE_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours (1 day)

// Local storage key for currency preference
const CURRENCY_PREFERENCE_KEY = 'currency_preference';

// Global promise to prevent concurrent API calls
let exchangeRatePromise: Promise<number | null> | null = null;

/**
 * Fetch latest USD to JPY exchange rate
 */
export const fetchExchangeRate = async (): Promise<number | null> => {
  // Return existing promise if one is already in progress
  if (exchangeRatePromise) {
    debugLog('Using existing exchange rate promise');
    return exchangeRatePromise;
  }

  // Create new promise and store it globally
  exchangeRatePromise = (async () => {
    try {
      // Check cache first
      const cachedRate = getCache<number>(EXCHANGE_RATE_CACHE_KEY);
      if (cachedRate) {
        debugLog('Using cached exchange rate:', cachedRate);
        return cachedRate;
      }

      // Check if we recently failed to avoid spam
      const failedCacheKey = 'exchange_rate_failed';
      const recentFailure = getCache<boolean>(failedCacheKey);
      if (recentFailure) {
        debugLog('Recently failed to fetch exchange rate, using fallback');
        const fallbackRate = 145.0;
        return fallbackRate;
      }

    // Fetch from open.er-api.com
    const url = 'https://open.er-api.com/v6/latest/USD';

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // Add timeout to prevent hanging
      signal: AbortSignal.timeout(5000) // 5 second timeout
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: ExchangeRateResponse = await response.json();
    
    if (data.result !== 'success') {
      throw new Error('API returned error result');
    }

    // Get JPY rate
    const rate = data.rates.JPY;
    
    if (!rate) {
      throw new Error('JPY rate not found in response');
    }
    
    // Cache the rate
    saveCache(EXCHANGE_RATE_CACHE_KEY, rate, EXCHANGE_RATE_CACHE_DURATION);
    
      debugLog('Fetched new exchange rate:', rate, 'at', data.time_last_update_utc);
      return rate;

    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
      
      // Cache the failure to prevent repeated attempts for 24 hours (same as normal cache)
      saveCache('exchange_rate_failed', true, 24 * 60 * 60 * 1000);
      
      // Return a fallback rate if fetch fails
      const fallbackRate = 145.0; // Approximate rate as fallback
      console.warn('Using fallback exchange rate:', fallbackRate);
      
      // Cache the fallback rate for the same duration (24 hours)
      saveCache(EXCHANGE_RATE_CACHE_KEY, fallbackRate, 24 * 60 * 60 * 1000);
      
      return fallbackRate;
    } finally {
      // Clear the promise when done (success or failure)
      exchangeRatePromise = null;
    }
  })();

  return exchangeRatePromise;
};

/**
 * Convert USD amount to JPY
 */
export const convertUsdToJpy = async (usdAmount: number): Promise<number> => {
  const rate = await fetchExchangeRate();
  if (!rate) return usdAmount; // Fallback to USD if conversion fails
  
  return usdAmount * rate;
};

/**
 * Format currency amount based on currency type
 */
export const formatCurrency = (amount: number, currency: Currency = 'USD'): string => {
  if (isNaN(amount) || !isFinite(amount)) return currency === 'USD' ? '$0.00' : '¥0';
  
  if (currency === 'USD') {
    return `$${amount.toFixed(2)}`;
  } else {
    return `¥${Math.round(amount).toLocaleString()}`;
  }
};

/**
 * Get user's preferred currency from local storage
 */
export const getCurrencyPreference = (): Currency => {
  try {
    const stored = localStorage.getItem(CURRENCY_PREFERENCE_KEY);
    if (stored === 'JPY' || stored === 'USD') {
      return stored;
    }
  } catch (error) {
    console.error('Failed to get currency preference:', error);
  }
  return 'USD'; // Default to USD
};

/**
 * Save user's preferred currency to local storage
 */
export const setCurrencyPreference = (currency: Currency): void => {
  try {
    localStorage.setItem(CURRENCY_PREFERENCE_KEY, currency);
  } catch (error) {
    console.error('Failed to save currency preference:', error);
  }
};

/**
 * Toggle between USD and JPY
 */
export const toggleCurrency = (currentCurrency: Currency): Currency => {
  return currentCurrency === 'USD' ? 'JPY' : 'USD';
};

/**
 * Convert amount based on source and target currency
 */
export const convertCurrency = async (
  amount: number, 
  fromCurrency: Currency, 
  toCurrency: Currency
): Promise<number> => {
  if (fromCurrency === toCurrency) {
    return amount;
  }
  
  if (fromCurrency === 'USD' && toCurrency === 'JPY') {
    return await convertUsdToJpy(amount);
  }
  
  if (fromCurrency === 'JPY' && toCurrency === 'USD') {
    const rate = await fetchExchangeRate();
    if (!rate) return amount;
    return amount / rate;
  }
  
  return amount;
};